import type { DeckCard } from '@/hooks/useDecks';

export type LegalitySeverity = 'error' | 'warning' | 'info';

export interface LegalityIssue {
  severity: LegalitySeverity;
  message: string;
}

export interface LegalityReport {
  legal: boolean; // no errors
  issues: LegalityIssue[];
  totalCards: number;
  inkableCount: number;
  characterCount: number;
  uniqueInks: string[];
  bannedCards: string[];
}

/**
 * Standard Lorcana Core constructed format checks.
 * - Exactly 60 cards
 * - Max 4 copies per card (already enforced when adding)
 * - Exactly 2 inks
 * - Soft warnings: ≥24 inkable, ≥20 characters, curve advice
 * - Format-aware: flag any card whose `legality_core` !== 'legal'
 */
export interface LegalityOptions {
  /** Set names currently rotated OUT of Core (Archive). Cards from these sets are illegal in Core. */
  archiveSetNames?: Set<string>;
  /** Set names that will rotate out on the next release (typically the oldest current-Core set). Soft warning. */
  rotatingSoonSetNames?: Set<string>;
}

export function validateDeck(
  deckCards: DeckCard[],
  expectedInks: [string, string],
  options?: LegalityOptions,
) {

  const issues: LegalityIssue[] = [];
  const totalCards = deckCards.reduce((s, dc) => s + dc.quantity, 0);

  let inkableCount = 0;
  let characterCount = 0;
  const inkSet = new Set<string>();
  const bannedCards: string[] = [];
  const archiveQtyBySet = new Map<string, number>();
  const rotatingQtyBySet = new Map<string, number>();

  for (const dc of deckCards) {
    const c = dc.card;
    if (!c) continue;
    if (c.inkable) inkableCount += dc.quantity;
    if (c.card_type === 'Character') characterCount += dc.quantity;
    if (c.ink_color) inkSet.add(c.ink_color);
    if (dc.quantity > 4) {
      issues.push({ severity: 'error', message: `${c.name}: ${dc.quantity} copies (max 4)` });
    }
    const legality = (c as any).legality_core ?? 'legal';
    if (legality && legality !== 'legal') {
      bannedCards.push(c.name);
      issues.push({ severity: 'error', message: `${c.name} is ${legality} in Core` });
    }
    const setName = (c as any).set_name as string | undefined;
    if (setName) {
      if (options?.archiveSetNames?.has(setName)) {
        archiveQtyBySet.set(setName, (archiveQtyBySet.get(setName) || 0) + dc.quantity);
      } else if (options?.rotatingSoonSetNames?.has(setName)) {
        rotatingQtyBySet.set(setName, (rotatingQtyBySet.get(setName) || 0) + dc.quantity);
      }
    }
  }

  // Rotation: archive sets are illegal in Core.
  archiveQtyBySet.forEach((qty, name) => {
    issues.push({
      severity: 'error',
      message: `${qty} card${qty === 1 ? '' : 's'} from ${name} — rotated to Archive (not Core-legal)`,
    });
  });
  // Rotating soon: soft warning so the user can plan.
  rotatingQtyBySet.forEach((qty, name) => {
    issues.push({
      severity: 'warning',
      message: `${qty} card${qty === 1 ? '' : 's'} from ${name} rotate out on the next set release`,
    });
  });


  // Card count
  if (totalCards < 60) {
    issues.push({ severity: 'error', message: `Need ${60 - totalCards} more card${60 - totalCards === 1 ? '' : 's'} (60 required)` });
  } else if (totalCards > 60) {
    issues.push({ severity: 'error', message: `${totalCards - 60} card${totalCards - 60 === 1 ? '' : 's'} over the 60-card limit` });
  }

  // Ink composition
  const inksUsed = [...inkSet];
  const allowed = new Set(expectedInks);
  for (const ink of inksUsed) {
    if (!allowed.has(ink)) {
      issues.push({ severity: 'error', message: `Contains ${ink} but deck is ${expectedInks.join(' / ')}` });
    }
  }
  if (inksUsed.length > 2) {
    issues.push({ severity: 'error', message: `Uses ${inksUsed.length} inks (max 2)` });
  }

  // Soft warnings
  if (totalCards > 0) {
    if (inkableCount < 24) {
      issues.push({ severity: 'warning', message: `Only ${inkableCount} inkable card${inkableCount === 1 ? '' : 's'} (recommend ≥24)` });
    }
    if (characterCount < 20) {
      issues.push({ severity: 'warning', message: `Only ${characterCount} character${characterCount === 1 ? '' : 's'} (recommend ≥20)` });
    }
  }

  return {
    legal: !issues.some(i => i.severity === 'error'),
    issues,
    totalCards,
    inkableCount,
    characterCount,
    uniqueInks: inksUsed,
    bannedCards,
  };
}

/**
 * Dreamborn / TTS-style text export.
 * Format: "<qty> <Name> - <Subtitle>" one per line, sorted by ink then cost.
 */
export function exportDeckAsText(
  deckName: string,
  deckCards: DeckCard[]
): string {
  const sorted = sortForExport(deckCards);
  const lines = [`# ${deckName}`, ''];
  for (const dc of sorted) {
    if (!dc.card) continue;
    const sub = dc.card.subtitle ? ` - ${dc.card.subtitle}` : '';
    lines.push(`${dc.quantity} ${dc.card.name}${sub}`);
  }
  return lines.join('\n');
}

/** JSON export (Dreamborn-compatible shape). */
export function exportDeckAsJson(
  deckName: string,
  inks: [string, string],
  deckCards: DeckCard[],
): string {
  const sorted = sortForExport(deckCards);
  return JSON.stringify(
    {
      name: deckName,
      format: 'core',
      inks,
      cards: sorted
        .filter(dc => dc.card)
        .map(dc => ({
          name: dc.card!.name,
          subtitle: dc.card!.subtitle ?? null,
          ink: dc.card!.ink_color,
          cost: dc.card!.cost,
          quantity: dc.quantity,
        })),
    },
    null,
    2,
  );
}

/** CSV export — useful for spreadsheets, Inkdecks, and bulk-edit tools. */
export function exportDeckAsCsv(deckCards: DeckCard[]): string {
  const rows = [['Quantity', 'Name', 'Subtitle', 'Ink', 'Cost', 'Rarity', 'Type']];
  for (const dc of sortForExport(deckCards)) {
    if (!dc.card) continue;
    const c = dc.card;
    rows.push([
      String(dc.quantity),
      csvField(c.name),
      csvField(c.subtitle ?? ''),
      c.ink_color ?? '',
      String(c.cost ?? ''),
      c.rarity ?? '',
      c.card_type ?? '',
    ]);
  }
  return rows.map(r => r.join(',')).join('\n');
}

function csvField(s: string): string {
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function sortForExport(deckCards: DeckCard[]): DeckCard[] {
  return [...deckCards].sort((a, b) => {
    const aInk = a.card?.ink_color || '';
    const bInk = b.card?.ink_color || '';
    if (aInk !== bInk) return aInk.localeCompare(bInk);
    const aCost = a.card?.cost ?? 0;
    const bCost = b.card?.cost ?? 0;
    if (aCost !== bCost) return aCost - bCost;
    return (a.card?.name || '').localeCompare(b.card?.name || '');
  });
}

/**
 * Map each error-severity legality issue to the deck card it refers to so the
 * UI can render an inline "Fix" action next to the warning row.
 */
export interface LegalityFix {
  cardId: string;
  cardName: string;
  action: 'trim-to-4' | 'remove-all' | 'remove-one';
  label: string;
}

export function buildLegalityFixes(
  report: LegalityReport,
  deckCards: DeckCard[],
): Record<string, LegalityFix> {
  const fixes: Record<string, LegalityFix> = {};
  for (const issue of report.issues) {
    if (issue.severity !== 'error') continue;
    // Try to match the issue to a card by leading "<name>:" prefix used in validateDeck.
    const colonIdx = issue.message.indexOf(':');
    const namePart = colonIdx > 0 ? issue.message.slice(0, colonIdx).trim() : null;
    const banPart = issue.message.match(/^(.*?) is (banned|restricted) in Core$/i);
    const targetName = namePart ?? (banPart ? banPart[1].trim() : null);
    if (!targetName) continue;
    const dc = deckCards.find(d => d.card?.name === targetName);
    if (!dc || !dc.card) continue;
    if (banPart) {
      fixes[issue.message] = {
        cardId: dc.card_id,
        cardName: dc.card.name,
        action: 'remove-all',
        label: 'Remove all',
      };
    } else if (dc.quantity > 4) {
      fixes[issue.message] = {
        cardId: dc.card_id,
        cardName: dc.card.name,
        action: 'trim-to-4',
        label: 'Trim to 4',
      };
    } else {
      fixes[issue.message] = {
        cardId: dc.card_id,
        cardName: dc.card.name,
        action: 'remove-one',
        label: 'Remove 1',
      };
    }
  }
  return fixes;
}

